import { useElementSize, useSelectableList } from '@fullstory/react-hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ListItem } from '../../components/ListItem';
import { ListItemText } from '../../components/ListItemText';
import { Popover } from '../../components/Popover';
import { TextInput } from '../../components/TextInput';
import { Tooltip } from '../../components/Tooltip';
import { identifyComponent } from '../../util';
import { Select } from '../Select';

export type AutoCompleteOptionType = {
  id: string;
  label: string;
  leftAdornment?: React.ReactNode;
  subtext?: string;
  tooltip?: Omit<React.ComponentProps<typeof Tooltip>, 'children' | 'compact'>;
  disabled?: boolean;
};

type AutoCompleteProps<T extends AutoCompleteOptionType> = {
  /** Default value for the text input */
  inputValue: string;
  /** Function called when text changes */
  onInputChange: (l: string) => void;
  /** Function called when the input blurs */
  onInputBlur?: () => void;
  /** The selected option from the available options */
  selected: T | null;
  /** Function called upon selecting an item from the autocomplete */
  onSelect: (s: T) => void;
  /** Possible values that will appear as a use types */
  options: T[];
  /** Placeholder text for context */
  placeholder?: string;
  /** Whether or not the input is invalid. */
  inputInvalid?: boolean;
  /** Whether the No Results state should be hidden when the input matches no options. */
  hideNoResults?: boolean;
  /** If disabled, the autocomplete cannot be modified */
  disabled?: boolean;
  /** Height of the menu before a scroll bar appears. Can be any valid CSS object value. */
  maxHeight?: number | string;
  /** A handle used during testing to refer to the input */
  testId?: string;
  /** Optional callback that happens on focus */
  onFocus?: () => void;
  /** Optional width of the popover, used to override the size of the input */
  popoverWidth?: number;
  /** Optional boolean to leave popover open when selecting an option */
  persistPopoverOnSelect?: boolean;
  /** If true, auto focuses the input on initial render */
  autoFocusInput?: boolean;
};

export function AutoComplete<T extends AutoCompleteOptionType>({
  inputValue,
  onInputChange,
  onInputBlur,
  onSelect,
  options,
  placeholder = '',
  selected,
  inputInvalid,
  hideNoResults,
  disabled,
  maxHeight = 300,
  testId,
  onFocus,
  popoverWidth,
  persistPopoverOnSelect = false,
  autoFocusInput,
}: AutoCompleteProps<T>) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const { getListItemProps } = useSelectableList(
    options,
    (maybeOption, option) => maybeOption.id === option.id,
    s => {
      onSelect(s);
      setOpen(false);
    },
    {
      active: open,
      alwaysHovered: true,
    },
  );
  const [inputElement, setInputElement] = useState<HTMLInputElement | null>(null);
  const { elementSize: inputSize, ref: inputRef } = useElementSize();
  const ref = (element: HTMLInputElement | null) => {
    setInputElement(element);
    inputRef(element);
  };

  // only on first render, if autoFocus is true, focus the input
  const [shouldFocus, setShouldFocus] = useState(autoFocusInput);
  useEffect(() => {
    if (shouldFocus && inputElement) {
      inputElement.focus();
      setShouldFocus(false);
    }
  }, [inputElement]);

  const cancel = useCallback(() => {
    inputElement?.blur();
    setOpen(false);
    onInputChange(selected == null ? '' : selected.label);
  }, [inputElement, onInputChange, selected]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
        case 'Tab':
          cancel();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cancel]);

  return (
    <>
      <TextInput
        onFocus={() => {
          onFocus?.();
          setOpen(true);
        }}
        onChange={e => onInputChange(e.target.value)}
        onBlur={onInputBlur}
        placeholder={placeholder}
        ref={ref}
        onKeyDown={e =>
          e.key === 'Enter' && persistPopoverOnSelect
            ? setTimeout(() => setOpen(true), 0)
            : undefined
        }
        value={inputValue}
        invalid={inputInvalid}
        disabled={disabled}
        testId={testId}
        {...identifyComponent('auto-complete')}
      />
      <Popover
        anchor={open ? inputElement : null}
        onCloseSignal={cancel}
        placement="bottom-start"
        offset={16}
        open={open && (options.length > 0 || !hideNoResults)}
        width={popoverWidth || inputSize?.width}
        maxHeight={maxHeight}
        overflow="scroll"
      >
        <Select.Options>
          {options.length ? (
            options.map(option =>
              option.tooltip ? (
                <Tooltip key={option.id} {...option.tooltip}>
                  <Select.Option
                    {...getListItemProps(option)}
                    selected={selected?.id === option.id}
                    leftAdornment={option.leftAdornment}
                    subtext={option.subtext}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </Select.Option>
                </Tooltip>
              ) : (
                <Select.Option
                  key={option.id}
                  {...getListItemProps(option)}
                  selected={selected?.id === option.id}
                  leftAdornment={option.leftAdornment}
                  subtext={option.subtext}
                  disabled={option.disabled}
                >
                  {option.label}
                </Select.Option>
              ),
            )
          ) : (
            <ListItem>
              <ListItemText
                text={intl.formatMessage({ defaultMessage: 'No results.', id: '0H2hdf' })}
              />
            </ListItem>
          )}
        </Select.Options>
      </Popover>
    </>
  );
}
